import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { useAree, useCatalogo, useSessione } from '../../servizi/sessione.tsx'
import { Attesa, Avviso, Dato, Vuoto } from '../../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, Campo, Etichetta, EtichettaArea, Modale, Paginazione, Riquadro, Tabella, totaleDi,
} from '../../componenti/base.tsx'
import { IconaIndietro, IconaLucchetto, IconaPiu } from '../../componenti/Icone.tsx'
import { data, leggibile, oggi, tonoIntervento, tonoPercorso } from './comune.ts'
import { durataBreve } from '../../servizi/regole-intervento.ts'

export type Percorso = {
  id: string; tipologia_codice: string; tipologia: string; area_codice: string
  stato: string; data_apertura: string; data_chiusura: string | null; interventi: string
  sede_id: string | null; progetto_id: string | null; operatore_riferimento: string | null
}

/** Riga di GET /interventi. `totale` e' la colonna `count(*) over ()` della stessa query. */
type Intervento = {
  id: string; data: string; durata_minuti: number | null; modalita: string
  tipologia_codice: string; tipologia: string; area_codice: string; stato: string
  operatore_id: string | null; operatore_nome: string | null; operatore_cognome: string | null
  ore_mediazione?: number | null; ore_mediazione_da_verificare?: boolean | null
  /** Somma delle ore di mediazione sulle righe filtrate, come `totale` (D-27). */
  ore_mediazione_totali?: number | null
  totale: string
}

/**
 * Sezione «Percorsi» della scheda: l'elenco, e il percorso aperto al suo posto.
 *
 * Il percorso scelto e' stato di questa sezione: cambiando linguetta il componente si smonta e
 * si torna all'elenco, come faceva il guscio azzerando la scelta a ogni cambio di sezione.
 *
 * «Apri un percorso» sta nell'intestazione, sempre, per chi puo' aprire percorsi e registrare
 * interventi sulla scheda (segnalazione di CIDIS del 19/10/2026: compariva solo sulla scheda senza
 * percorsi). Il percorso nasce con il suo primo intervento: porta al modulo con «nuovo percorso»
 * gia' scelto.
 */
export function SezionePercorsi({ personaId, percorsi, puoRegistrareIntervento, puoAprirePercorso, ricarica }: {
  personaId: string
  percorsi: Percorso[]
  puoRegistrareIntervento: boolean
  puoAprirePercorso: boolean
  ricarica: () => Promise<void>
}) {
  const aree = useAree()
  const [percorsoScelto, setPercorsoScelto] = useState<string | null>(null)
  // Il percorso si ricava dall'elenco a ogni disegno: dopo una chiusura la scheda si ricarica
  // e la sezione mostra il nuovo stato senza tenere una copia da aggiornare a parte.
  const percorsoApertoOra = percorsi.find((p) => p.id === percorsoScelto) ?? null

  if (percorsoApertoOra) {
    return (
      <DettaglioPercorso
        key={percorsoApertoOra.id}
        personaId={personaId}
        percorso={percorsoApertoOra}
        puoRegistrareIntervento={puoRegistrareIntervento}
        torna={() => setPercorsoScelto(null)}
        ricarica={ricarica}
      />
    )
  }

  return (
    <Riquadro denso titolo="Percorsi" azioni={puoAprirePercorso
      ? (
        <Link className="bottone bottone-secondario" to={`/persone/${personaId}/intervento?percorso=nuovo`}>
          <IconaPiu /> Apri un percorso
        </Link>
      )
      : undefined}>
      <Tabella
        intestazioni={['Area', 'Tipologia', 'Apertura', 'Stato', 'Interventi',
          <span key="comandi" className="solo-lettore">Comandi</span>]}
        vuoto={<Vuoto testo={puoAprirePercorso
          ? 'Nessun percorso aperto. Il percorso si apre con «Apri un percorso», insieme al suo primo intervento.'
          : 'Nessun percorso aperto.'} />}
      >
        {percorsi.map((p) => (
          <tr key={p.id}>
            <td data-etichetta="Area">
              <EtichettaArea codice={p.area_codice} colore={aree.coloreDi(p.area_codice)} />
            </td>
            <td data-etichetta="Tipologia">{p.tipologia}</td>
            <td data-etichetta="Apertura">{data(p.data_apertura)}</td>
            <td data-etichetta="Stato">
              <Etichetta tono={tonoPercorso(p.stato)}>{leggibile(p.stato)}</Etichetta>
            </td>
            <td data-etichetta="Interventi" className="mono">{p.interventi}</td>
            <td data-etichetta="Comandi">
              <Bottone variante="secondario" dimensione="piccolo"
                       onClick={() => setPercorsoScelto(p.id)}>Apri</Bottone>
            </td>
          </tr>
        ))}
      </Tabella>
    </Riquadro>
  )
}

// --- percorso aperto ------------------------------------------------------------------------

const LIMITE_INTERVENTI = 20

/**
 * Estremi di un percorso e i suoi interventi.
 *
 * Gli interventi arrivano da GET /interventi?percorso_id=..., che applica il perimetro di
 * 'intervento.leggi' incarico per incarico: l'elenco puo' quindi essere piu' corto del
 * conteggio mostrato nella tabella dei percorsi, che conta tutte le righe non annullate.
 *
 * Il componente si monta con `key` uguale all'identificativo del percorso: cambiando percorso
 * si riparte dalla prima pagina invece di trascinare lo scorrimento del percorso precedente.
 */
function DettaglioPercorso({
  personaId, percorso, puoRegistrareIntervento, torna, ricarica,
}: {
  personaId: string
  percorso: Percorso
  puoRegistrareIntervento: boolean
  torna: () => void
  ricarica: () => Promise<void>
}) {
  const { io, puo, tassonomia } = useSessione()
  const aree = useAree()
  const modalita = useCatalogo('modalita_erogazione')
  const [interventi, setInterventi] = useState<Intervento[] | null>(null)
  const [salta, setSalta] = useState(0)
  const [totaleNoto, setTotaleNoto] = useState<number | null>(null)
  const [oreMediazione, setOreMediazione] = useState<number | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [chiusuraAperta, setChiusuraAperta] = useState(false)
  const [interventoAperto, setInterventoAperto] = useState<string | null>(null)

  // Senza 'intervento.leggi' in nessun incarico la rotta risponde 404: la richiesta non si fa
  // e al suo posto si dichiara che l'elenco non e' consultabile.
  const leggeInterventi = puo('intervento.leggi')
  const chiuso = percorso.stato === 'chiuso'
  // Ore di mediazione (D-27): colonna e somma solo sui percorsi di tipologie che le chiedono.
  const conOre = !!tassonomia?.tipologie.find((t) => t.codice === percorso.tipologia_codice)?.con_ore_mediazione

  useEffect(() => {
    if (!leggeInterventi) return
    let vivo = true
    setInterventi(null)
    api.get<Intervento[]>(
      `/interventi?percorso_id=${percorso.id}&limite=${LIMITE_INTERVENTI}&salta=${salta}`)
      .then((righe) => {
        if (!vivo) return
        setInterventi(righe)
        const letto = totaleDi(righe)
        if (letto !== null) setTotaleNoto(letto)
        // Come il totale, la somma arriva solo con le righe: su una pagina vuota resta l'ultima.
        if (righe.length) setOreMediazione(righe[0].ore_mediazione_totali ?? 0)
      })
      .catch((e) => {
        if (vivo) setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
      })
    return () => { vivo = false }
  }, [percorso.id, salta, leggeInterventi])

  // Una pagina vuota non porta la colonna `totale`: leggerla come zero faceva scrivere
  // «Righe 0–0 di 0» al primo passo oltre l'ultima pagina, che e' il momento in cui il
  // conteggio serve per tornare indietro. Si conserva percio' l'ultimo valore noto.
  const totale = totaleDi(interventi) ?? totaleNoto

  /**
   * Nome dell'operatore di riferimento. La scheda ne restituisce solo l'identificativo, quindi
   * il nome si ricava da un intervento dello stesso percorso registrato da quella persona,
   * oppure dalla sessione quando l'operatore di riferimento e' chi sta guardando. Se non
   * compare in nessuna delle due fonti resta il trattino.
   */
  const operatore = useMemo(() => {
    const riferimento = percorso.operatore_riferimento
    if (!riferimento) return null
    if (io?.id === riferimento) return `${io.nome} ${io.cognome}`
    const suo = interventi?.find((i) => i.operatore_id === riferimento && i.operatore_nome)
    return suo ? `${suo.operatore_nome} ${suo.operatore_cognome ?? ''}`.trim() : null
  }, [percorso.operatore_riferimento, io, interventi])

  return (
    <>
      <Riquadro
        titolo={percorso.tipologia}
        azioni={
          <>
            <Bottone variante="secondario" icona={<IconaIndietro />} onClick={torna}>
              Torna ai percorsi
            </Bottone>
            {/* La rotta di chiusura accetta qualunque percorso non gia' chiuso, quindi anche
                quelli sospesi. Il permesso e' 'percorso.gestisci'. */}
            {!chiuso && puo('percorso.gestisci') && (
              <Bottone variante="critico" icona={<IconaLucchetto />}
                       onClick={() => setChiusuraAperta(true)}>
                Chiudi percorso
              </Bottone>
            )}
            {!chiuso && puoRegistrareIntervento && (
              <Link className="bottone bottone-primario" to={`/persone/${personaId}/intervento?percorso=${percorso.id}`}>
                <IconaPiu /> Registra intervento
              </Link>
            )}
          </>
        }
      >
        <dl className="elenco-dati">
          <Dato nome="Area">
            <EtichettaArea codice={percorso.area_codice} colore={aree.coloreDi(percorso.area_codice)} />
          </Dato>
          <Dato nome="Tipologia">{percorso.tipologia}</Dato>
          <Dato nome="Apertura">{data(percorso.data_apertura)}</Dato>
          <Dato nome="Stato">
            <Etichetta tono={tonoPercorso(percorso.stato)}>{leggibile(percorso.stato)}</Etichetta>
          </Dato>
          {chiuso && <Dato nome="Chiusura">{data(percorso.data_chiusura)}</Dato>}
          {conOre && (
            <Dato nome="Ore di mediazione">
              <span className="mono">{oreMediazione ?? '—'}</span>
            </Dato>
          )}
          {percorso.operatore_riferimento && (
            <Dato nome="Operatore di riferimento">{operatore}</Dato>
          )}
        </dl>
      </Riquadro>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {!leggeInterventi
        ? (
          <Riquadro>
            <p className="tenue piccolo">
              Gli interventi di questo percorso non rientrano negli incarichi in corso.
            </p>
          </Riquadro>
        )
        : !interventi
          ? <Attesa testo="Caricamento degli interventi…" />
          : (
            <Riquadro denso>
              <Tabella
                intestazioni={['Data', 'Tipologia', 'Durata', ...(conOre ? ['Ore di mediazione'] : []),
                  'Modalità', 'Operatore', 'Stato',
                  <span key="comandi" className="solo-lettore">Comandi</span>]}
                vuoto={<Vuoto testo="Nessun intervento registrato su questo percorso." />}
              >
                {interventi.map((i) => (
                  <tr key={i.id}>
                    <td data-etichetta="Data">{data(i.data)}</td>
                    <td data-etichetta="Tipologia">{i.tipologia}</td>
                    <td data-etichetta="Durata" className="mono">{durataBreve(i.durata_minuti)}</td>
                    {conOre && (
                      <td data-etichetta="Ore di mediazione">
                        <span className="mono">{i.ore_mediazione ?? '—'}</span>
                        {i.ore_mediazione_da_verificare && (
                          <> <Etichetta tono="attenzione">da verificare</Etichetta></>
                        )}
                      </td>
                    )}
                    <td data-etichetta="Modalità">{modalita.nomeDi(i.modalita)}</td>
                    <td data-etichetta="Operatore">
                      {i.operatore_nome
                        ? `${i.operatore_nome} ${i.operatore_cognome ?? ''}`.trim()
                        : <span className="tenue">—</span>}
                    </td>
                    <td data-etichetta="Stato">
                      <Etichetta tono={tonoIntervento(i.stato)}>{leggibile(i.stato)}</Etichetta>
                    </td>
                    <td data-etichetta="Comandi">
                      <Bottone variante="secondario" dimensione="piccolo"
                               onClick={() => setInterventoAperto(i.id)}>Apri</Bottone>
                    </td>
                  </tr>
                ))}
              </Tabella>
              <Paginazione salta={salta} limite={LIMITE_INTERVENTI} totale={totale}
                           ricevute={interventi.length} cambia={setSalta} />
            </Riquadro>
          )}

      {interventoAperto && (
        <SchedaIntervento id={interventoAperto} chiudi={() => setInterventoAperto(null)} />
      )}

      {chiusuraAperta && (
        <ModaleChiusura
          percorso={percorso}
          annulla={() => setChiusuraAperta(false)}
          chiusa={() => { setChiusuraAperta(false); void ricarica() }}
        />
      )}
    </>
  )
}

// --- scheda di un intervento ----------------------------------------------------------------

type DettaglioIntervento = {
  id: string; data: string; durata_minuti: number | null; modalita: string
  tipologia: string; tipologia_codice: string; area_codice: string
  sottotipologia_codice: string | null; tema_codice: string | null
  stato: string; esito: string | null
  dati: Record<string, unknown>; form_versione: number | null
  /** Risposte del modulo con le etichette della versione usata (D-26). */
  risposte?: { campo: string; etichetta: string; testo: string }[]
  ore_mediazione?: number | null; ore_mediazione_da_verificare?: boolean | null
  progetto: string | null; sede: string | null; stakeholder: string | null
  attivita_istituzionale: boolean
  operatore_nome: string | null; operatore_cognome: string | null
  creato_il: string; aggiornato_il: string | null
  note: string | null
  /** Falso quando una nota esiste ma il perimetro sulla persona non la copre. */
  note_visibili: boolean
  persona_id: string
  /** Campi di chiusura (Note aggiuntive dell'11/09). */
  comune: string | null; comune_provincia: string | null
  categoria: string | null; categoria_nascosta: boolean
  fascia: string | null; fascia_eta_manuale: boolean
  ha_stakeholder: boolean | null
  /** Cosa manca per lo stato completo, sulle bozze e sulle righe migrate. */
  mancanti_chiusura: { campo: string; messaggio: string }[]
  modificabile: boolean
}

/**
 * Scheda di un intervento.
 *
 * Nota libera e risposte del modulo si scrivevano al momento della registrazione e non erano
 * piu' rileggibili: erano nel database e in nessuna schermata. Qui si aprono, e l'apertura
 * viene registrata nel registro eventi come 'intervento.letto'.
 */
function SchedaIntervento({ id, chiudi }: { id: string; chiudi: () => void }) {
  const modalita = useCatalogo('modalita_erogazione')
  const { tassonomia } = useSessione()
  const [dettaglio, setDettaglio] = useState<DettaglioIntervento | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    api.get<DettaglioIntervento>(`/interventi/${id}`)
      .then((d) => { if (vivo) setDettaglio(d) })
      .catch((e) => {
        if (vivo) setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
      })
    return () => { vivo = false }
  }, [id])

  // Le etichette di campi e voci le risolve l'API sulla versione del modulo usata: i codici
  // salvati in `dati` a video non direbbero nulla.
  const risposte = dettaglio?.risposte ?? []

  // Le bozze si completano dalla pagina di registrazione, con il modulo precompilato. Le righe del
  // vecchio archivio non sono cose da fare (461): restano modificabili, ma senza invito a completarle.
  const completabile = dettaglio?.stato === 'bozza'
  const storico = dettaglio?.stato === 'storico_incompleto'

  return (
    <Modale
      titolo="Intervento" chiudi={chiudi} larga
      azioni={dettaglio?.modificabile && (
        <Link className={`bottone ${completabile ? 'bottone-primario' : 'bottone-secondario'}`}
              to={`/persone/${dettaglio.persona_id}/intervento/${dettaglio.id}`}>
          {completabile ? 'Completa l’intervento' : 'Modifica'}
        </Link>
      )}
    >
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !dettaglio && <Attesa testo="Caricamento…" />}
      {dettaglio && (
        <>
          {storico && (
            <Avviso tipo="informazione">
              Intervento dal vecchio archivio: i dati che allora non si registravano non vanno completati.
            </Avviso>
          )}
          {!storico && dettaglio.mancanti_chiusura.length > 0 && (
            <Avviso tipo="attenzione">
              <strong>Per lo stato completo mancano:</strong>
              <ul className="elenco-avviso">
                {dettaglio.mancanti_chiusura.map((m, i) => <li key={i}>{m.messaggio}</li>)}
              </ul>
            </Avviso>
          )}
          <dl className="elenco-dati">
            <Dato nome="Data">{data(dettaglio.data)}</Dato>
            <Dato nome="Tipologia">{dettaglio.tipologia}</Dato>
            {/* Facoltativa dalla 551: senza, il trattino di Dato. */}
            <Dato nome="Durata">{dettaglio.durata_minuti ? durataBreve(dettaglio.durata_minuti) : null}</Dato>
            {dettaglio.ore_mediazione != null && (
              <Dato nome="Ore di mediazione">
                {dettaglio.ore_mediazione}
                {dettaglio.ore_mediazione_da_verificare && (
                  <> <Etichetta tono="attenzione">da verificare</Etichetta></>
                )}
              </Dato>
            )}
            <Dato nome="Modalità">{modalita.nomeDi(dettaglio.modalita)}</Dato>
            <Dato nome="Operatore">
              {dettaglio.operatore_nome
                ? `${dettaglio.operatore_nome} ${dettaglio.operatore_cognome ?? ''}`.trim()
                : <span className="tenue">—</span>}
            </Dato>
            <Dato nome="Stato">
              <Etichetta tono={tonoIntervento(dettaglio.stato)}>
                {leggibile(dettaglio.stato)}
              </Etichetta>
            </Dato>
            {dettaglio.sede && <Dato nome="Sede">{dettaglio.sede}</Dato>}
            {/* Campi di chiusura: sempre presenti, con il trattino quando mancano. */}
            <Dato nome="Comune">
              {dettaglio.comune ? `${dettaglio.comune} (${dettaglio.comune_provincia ?? ''})` : null}
            </Dato>
            <Dato nome="Categoria di utenti">
              {dettaglio.categoria_nascosta
                ? <Etichetta tono="riservato">riservata</Etichetta>
                : dettaglio.categoria}
            </Dato>
            <Dato nome="Fascia d’età">
              {dettaglio.fascia
                ? <>{dettaglio.fascia}{dettaglio.fascia_eta_manuale && <> <Etichetta tono="neutro">scelta a mano</Etichetta></>}</>
                : null}
            </Dato>
            <Dato nome="Ente coinvolto">
              {dettaglio.ha_stakeholder == null ? null
                : dettaglio.ha_stakeholder ? (dettaglio.stakeholder ?? 'Sì, ente non indicato') : 'No'}
            </Dato>
            <Dato nome="Progetto">
              {dettaglio.progetto ?? (dettaglio.attivita_istituzionale ? 'Attività istituzionale' : null)}
            </Dato>
            {/* Il tema non si chiede piu' dal 4/11/2026: quello gia' registrato resta leggibile. */}
            {dettaglio.tema_codice && (
              <Dato nome="Tema">
                {tassonomia?.temi.find((t) => t.codice === dettaglio.tema_codice)?.nome ?? dettaglio.tema_codice}
              </Dato>
            )}
            {dettaglio.esito && <Dato nome="Esito">{dettaglio.esito}</Dato>}
          </dl>

          {risposte.length > 0 && (
            <Riquadro titolo="Risposte del modulo" denso>
              <dl className="elenco-dati">
                {risposte.map((r) => <Dato key={r.campo} nome={r.etichetta}>{r.testo}</Dato>)}
              </dl>
            </Riquadro>
          )}

          <Riquadro titolo="Nota dell’operatore" denso>
            {dettaglio.note
              ? <p className="testo-libero">{dettaglio.note}</p>
              : (
                <p className="tenue piccolo">
                  {dettaglio.note_visibili
                    ? 'Nessuna nota su questo intervento.'
                    : 'La nota non rientra negli incarichi in corso su questa scheda.'}
                </p>
              )}
          </Riquadro>
        </>
      )}
    </Modale>
  )
}

/**
 * Chiusura di un percorso.
 *
 * POST /percorsi/:id/chiudi vuole `motivo_chiusura` (da 3 a 300 caratteri), e accetta `esito`
 * e `data_chiusura`. La data e' facoltativa per l'API, ma omessa lascia il percorso chiuso
 * senza data di chiusura: qui e' sempre valorizzata e parte da oggi. Non puo' precedere
 * l'apertura, vincolo scritto nella tabella e non verificato dall'API: il campo la limita.
 */
function ModaleChiusura({ percorso, annulla, chiusa }: {
  percorso: Percorso
  annulla: () => void
  chiusa: () => void
}) {
  const [motivo, setMotivo] = useState('')
  const [esito, setEsito] = useState('')
  const [dataChiusura, setDataChiusura] = useState(oggi())
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  async function conferma() {
    setInCorso(true); setErrore(null)
    try {
      await api.post(`/percorsi/${percorso.id}/chiudi`, {
        motivo_chiusura: motivo.trim(),
        esito: esito.trim() || null,
        data_chiusura: dataChiusura,
      })
      chiusa()
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
    } finally { setInCorso(false) }
  }

  return (
    <Modale
      titolo="Chiusura del percorso"
      chiudi={annulla}
      azioni={
        <>
          <Bottone variante="secondario" onClick={annulla} disabled={inCorso}>Annulla</Bottone>
          <Bottone variante="critico" inCorso={inCorso} onClick={() => void conferma()}
                   disabled={motivo.trim().length < 3 || !dataChiusura}>
            Chiudi il percorso
          </Bottone>
        </>
      }
    >
      <Avviso tipo="attenzione">
        Un percorso chiuso non accetta altri interventi e non si riapre dall’interfaccia.
        La chiusura non riesce finché restano interventi in bozza.
      </Avviso>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      <Campo etichetta="Motivo della chiusura" valore={motivo} cambia={setMotivo} obbligatorio
             minLength={3} maxLength={300}
             segnaposto="es. obiettivi raggiunti, presa in carico conclusa"
             aiuto="Da 3 a 300 caratteri. Resta nella cronologia della scheda." />
      <Campo tipo="date" etichetta="Data di chiusura" valore={dataChiusura} cambia={setDataChiusura}
             obbligatorio min={percorso.data_apertura.slice(0, 10)}
             aiuto="Non può precedere la data di apertura del percorso." />
      <AreaTesto etichetta="Esito" valore={esito} cambia={setEsito} righe={3} maxLength={500}
                 aiuto="Facoltativo. Fino a 500 caratteri." />
    </Modale>
  )
}
